import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';

interface FaqCardProps {
  number: number;
  question: string;
  answer: string;
  /** When true, the oversized numeral takes its own row above on mobile and sits inline left on lg+. */
  variant?: 'home' | 'page';
}

/**
 * Editorial split FAQ card: oversized Lora numeral on the left,
 * question + thin divider + answer on the right.
 *
 * Used both in the homepage <FaqSection /> teaser and on the /faq page,
 * so the visual continuity is preserved.
 */
export default function FaqCard({ number, question, answer, variant = 'home' }: FaqCardProps) {
  const padded = String(number).padStart(2, '0');
  const variantClass = variant === 'page' ? 'lg:p-12' : 'lg:p-10';

  return (
    <article
      className={`group relative flex flex-col overflow-hidden rounded-2xl bg-white p-7 ring-1 ring-[#1A2B3C]/[0.06] shadow-[0_2px_12px_-2px_rgba(26,43,60,0.06)] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-12px_rgba(26,43,60,0.18)] sm:p-8 ${variantClass}`}
    >
      {/* Top accent stripe: burgundy → gold on hover */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-[#8B1538] transition-colors duration-500 group-hover:bg-[#C9A961]"
      />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[auto_1fr] lg:gap-10">
        {/* Numeral column */}
        <div className="flex flex-row items-baseline gap-3 lg:items-start">
          <span
            className="font-serif text-[3rem] font-semibold leading-none text-[#C9A961] lg:text-[4.5rem]"
            style={{ fontFamily: 'Lora, serif' }}
            aria-hidden
          >
            {padded}
          </span>
        </div>

        {/* Content column */}
        <div className="flex flex-col">
          <h3
            className="font-serif text-[1.25rem] font-bold leading-snug text-[#1A2B3C] sm:text-[1.375rem] lg:text-[1.625rem]"
            style={{ fontFamily: 'Lora, serif' }}
          >
            {question}
          </h3>
          <span aria-hidden className="my-4 block h-px w-full bg-[#1A2B3C]/10" />
          <div className="prose prose-sm max-w-none text-[#1A2B3C]/75 prose-p:my-2 prose-p:leading-[1.75] prose-strong:text-[#1A2B3C] lg:prose-base lg:prose-p:leading-[1.8]">
            <ReactMarkdown remarkPlugins={[remarkBreaks]} rehypePlugins={[rehypeRaw]}>
              {answer}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </article>
  );
}
